import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { LineReveal } from "./motion/Reveal";
import { IMG } from "../lib/site";
import { Link } from "react-router-dom";

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-navy grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMG.hero}
          alt="Haveli Braj Bhushanjee, Bundi"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
      </motion.div>

      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/85 pointer-events-none"/>

      {/* Top overline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="absolute top-32 left-6 md:left-10 z-10 flex items-center gap-4"
      >
        <span className="h-px w-14 bg-gold"/>
        <span className="overline text-gold">Est. c.1775 · Bundi, Rajasthan</span>
      </motion.div>

      {/* Hero text */}
      <motion.div style={{ y: textY }} className="relative z-10 min-h-[100svh] flex flex-col justify-end px-6 md:px-10 pb-28 md:pb-32">
        <div className="max-w-[1440px] mx-auto w-full">
          <LineReveal
            className="font-display display-punch text-bg leading-[0.92]"
            lineClassName="block text-[14pt]"
            lines={["Two hundred", "and fifty years", <span key="italic" className="italic text-gold">of quiet grandeur.</span>]}
            delay={0.35}
          />

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.6 }}
              className="md:col-span-5 text-bg/85 text-base md:text-lg leading-relaxed max-w-md"
            >
              A heritage haveli below the Bundi Palace — original Bundi School Frescos, courtyards, carved stone arches, and a splendid view of illuminated Palace.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.9 }}
              className="md:col-span-4 md:col-start-8 flex flex-wrap gap-3"
            >
              <Link to="/heritage-hotel#booking" className="btn-pill btn-primary" data-testid="hero-reserve">
                Reserve your stay <span aria-hidden>→</span>
              </Link>
              <Link to="/about" className="btn-pill" style={{ background: "transparent", color: "#F9F7F1", border: "1px solid rgba(249,247,241,0.55)" }} data-testid="hero-story">
                Our story
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.3, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 overline text-bg/70 flex items-center gap-3"
      >
        <span className="w-8 h-px bg-bg/60"/> Scroll
      </motion.div>
    </section>
  );
}
